import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Import new components
import LandingPageNew from './components/LandingPageNew';
import AuthPageNew from './components/AuthPageNew';
import AppLayout from './components/AppLayout';
import CheckerMainWrapper from './components/CheckerMainWrapper';
import ComplianceChat from './components/ComplianceChat';
import SettingsPageNew from './components/SettingsPageNew';
import DocumentRepositoryWrapper from './components/DocumentRepositoryWrapper';
import PricingPageNew from './components/PricingPageNew';
import FAQPageNew from './components/FAQPageNew';
import TermsPage from './components/TermsPage';
import PrivacyPage from './components/PrivacyPage';
import DisclaimerPage from './components/DisclaimerPage';
import LegalPage from './components/LegalPage';
import HowItWorksPage from './components/HowItWorksPage';
import FeedbackWidget from './components/FeedbackWidget';

// Import styles
import './styles/variables.css';
import './styles/global.css';

import { supabase } from './supabaseClient';

// Scroll to top on route change
function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return null;
}

// Protected route wrapper
function ProtectedRoute({ children, user, loading }) {
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [checkoutMessage, setCheckoutMessage] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState('free');
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  const [monthlyChecksUsed, setMonthlyChecksUsed] = useState(0);
  const [monthlyCheckLimit, setMonthlyCheckLimit] = useState(3);
  const [checksResetDate, setChecksResetDate] = useState(null);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const location = useLocation();

  // Check for existing session on mount
  useEffect(() => {
    // Check for checkout result in URL
    const params = new URLSearchParams(window.location.search);
    const checkoutResult = params.get('checkout');
    if (checkoutResult === 'success') {
      setCheckoutMessage({ type: 'success', text: 'Subscription successful! Welcome to Mindmark.' });
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (checkoutResult === 'cancelled') {
      setCheckoutMessage({ type: 'info', text: 'Checkout cancelled. You can subscribe anytime from your settings.' });
      window.history.replaceState({}, '', window.location.pathname);
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserProfile(session.user.id);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserProfile(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserProfile = async (userId) => {
    setProfileLoading(true);
    const { data, error } = await supabase
      .from('profiles')
      .select('subscription_status, subscription_tier, monthly_checks_used, monthly_check_limit, checks_reset_date, billing_interval')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('Failed to fetch user profile:', error);
      setProfileLoading(false);
      return;
    }

    // If profile doesn't exist, create one via API (bypasses RLS)
    if (!data) {
      const { data: session } = await supabase.auth.getSession();
      const userEmail = session?.session?.user?.email;

      try {
        const response = await fetch('/api/create-profile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: userId,
            email: userEmail
          })
        });

        if (response.ok) {
          const result = await response.json();
          if (result.profile) {
            applyProfileState(result.profile);
          }
        } else {
          console.error('Failed to create profile:', await response.text());
        }
      } catch (createError) {
        console.error('Failed to create profile:', createError);
      }
      setProfileLoading(false);
      return;
    }

    applyProfileState(data);
    setProfileLoading(false);
  };

  const applyProfileState = (profile) => {
    setSubscriptionStatus(profile.subscription_status || 'free');
    setSubscriptionTier(profile.subscription_tier || 'free');
    setMonthlyChecksUsed(profile.monthly_checks_used || 0);
    setMonthlyCheckLimit(profile.monthly_check_limit || 3);
    setChecksResetDate(profile.checks_reset_date || null);
    setBillingInterval(profile.billing_interval || 'monthly');
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSubscriptionStatus('free');
    setSubscriptionTier('free');
    setMonthlyChecksUsed(0);
    setMonthlyCheckLimit(3);
    setChecksResetDate(null);
    setBillingInterval('monthly');
  };

  const incrementChecksUsed = async () => {
    if (!user) return;

    // Fetch current value from database to avoid race conditions
    const { data: currentData, error: fetchError } = await supabase
      .from('profiles')
      .select('monthly_checks_used')
      .eq('id', user.id)
      .maybeSingle();

    if (fetchError) {
      console.error('Failed to fetch current checks count:', fetchError);
      return;
    }

    const currentCount = currentData?.monthly_checks_used || 0;
    const newCount = currentCount + 1;

    const { error: updateError } = await supabase
      .from('profiles')
      .update({ monthly_checks_used: newCount })
      .eq('id', user.id);

    if (updateError) {
      console.error('Failed to update checks count:', updateError);
      return;
    }

    setMonthlyChecksUsed(newCount);
  };

  const hasActiveSubscription = subscriptionStatus === 'active';
  const checksRemaining = monthlyCheckLimit - monthlyChecksUsed;
  const canPerformCheck = subscriptionTier === 'pro' || checksRemaining > 0;

  // Show loading screen while checking auth
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPageNew user={user} onLogout={handleLogout} />} />
        <Route path="/how-it-works" element={<HowItWorksPage user={user} onLogout={handleLogout} />} />
        <Route path="/faq" element={<FAQPageNew user={user} onLogout={handleLogout} />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/disclaimer" element={<DisclaimerPage />} />

        {/* Auth routes - redirect to checker if already logged in */}
        <Route
          path="/login"
          element={
            user ? <Navigate to="/checker" replace /> : <AuthPageNew mode="login" />
          }
        />
        <Route
          path="/signup"
          element={
            user ? <Navigate to="/checker" replace /> : <AuthPageNew mode="signup" />
          }
        />
        <Route
          path="/forgot-password"
          element={
            user ? <Navigate to="/checker" replace /> : <AuthPageNew mode="forgot" />
          }
        />
        <Route
          path="/reset-password"
          element={<AuthPageNew mode="reset" />}
        />

        {/* Protected routes with shared layout (sidebar persists) */}
        <Route
          element={
            <ProtectedRoute user={user} loading={loading}>
              <AppLayout
                user={user}
                onLogout={handleLogout}
                hasActiveSubscription={hasActiveSubscription}
                subscriptionTier={subscriptionTier}
                monthlyChecksUsed={monthlyChecksUsed}
                monthlyCheckLimit={monthlyCheckLimit}
                checksRemaining={checksRemaining}
                checksResetDate={checksResetDate}
                billingInterval={billingInterval}
                canPerformCheck={canPerformCheck}
                onCheckPerformed={incrementChecksUsed}
                subscriptionStatus={subscriptionStatus}
                checkoutMessage={checkoutMessage}
                onDismissCheckoutMessage={() => setCheckoutMessage(null)}
                profileLoading={profileLoading}
              />
            </ProtectedRoute>
          }
        >
          {/* Nested routes render in AppLayout's Outlet */}
          <Route path="/checker" element={<CheckerMainWrapper />} />
          <Route path="/chat" element={<ComplianceChat />} />
          <Route path="/documents" element={<DocumentRepositoryWrapper />} />
          <Route path="/settings" element={<SettingsPageNew />} />
          <Route path="/settings/account" element={<SettingsPageNew />} />
          <Route path="/settings/privacy" element={<SettingsPageNew />} />
          <Route path="/settings/billing" element={<SettingsPageNew />} />
          <Route path="/settings/delete-account" element={<SettingsPageNew />} />
        </Route>

        {/* Pricing page - public so visitors can see pricing */}
        <Route
          path="/pricing"
          element={
            <PricingPageNew user={user} onLogout={handleLogout} hasActiveSubscription={hasActiveSubscription} />
          }
        />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <FeedbackWidget userEmail={user?.email} />
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
